import _ from 'lodash'
import Backbone from 'backbone'
import template from './designer-dataset-field-chooser.hbs'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import DividedList from 'scoreboard/components/divided-list'
import renderFieldDropdown from 'scoreboard/datasets/render-field-dropdown'
import Dataset from 'scoreboard/datasets/model/dataset'
const { TOTAL } = Dataset

const ReportColumnChooser = Backbone.View.extend({

	tagName: 'form',

	className: 'tree-actions',

	events : {
		'click .custom' : 'onClickCustom',
		// It doesn't look like the `add-stack` class is applied anywhere?
		// Leaving this as to not break something, though
		'click .add-stack' : 'onFieldChosen'
	},

	/**
	 * When a user adds a new column to a report, this component displays in a
	 * tooltip and allows the user to choose what type of column to add
	 * (e.g. related items, a dataset field, etc)
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} [dataset] the dataset to use if rendering dataset fields
	 *
	 * @param {Object[]} [optionData] the options to use if rendering non-dataset options
	 *
	 * @param {String|Number} [selectedId] the initially selected option/field ID,
	 * defaults to the first option none specified
	 *
	 * @param {Object} [datasetDropdownOptions] extra options to be passed along
	 * to renderFieldDropdown(), which renders the dataset dropdown
	 *
	 * @param {String} [headerButtonLabel='report.designer.newCalculation'] label for
	 * the button in the tooltip header (which triggers a 'details' event)
	 *
	 * @param {Function} [onAdd] callback to be called after the user clicks
	 * the "add" button. If it returns a Promise, the buttons will going into
	 * a busy/loading state until the Promise resolves
	 */
	initialize(options = {}) {
		this.options = options
		this.dataset = this.options.dataset
		this.datasetDropdownOptions = this.options.datasetDropdownOptions || {}
		this.formView = new FormView({
			allowCleanSubmission: true,
			el: this.el
		})
		this.isRepeatByForm = !this.options.fieldIdOrColumnType

		const listData = this._generateListData()
		if (this.isRepeatByForm) {
			listData.options = _.filter(listData.options, o => o.id != TOTAL)
		}
		this.dividedList = new DividedList(listData)
	},

	_generateListData: function() {
		let options = this.options.optionData
		if (this.dataset && !options) {
			// #41513/#25508 be sure to include the visible linked fields
			let { allowedFieldIds, allowedVisibleLinkedFieldIds } = this.datasetDropdownOptions
			let optionData = renderFieldDropdown.datasetFieldsToOptions(this.dataset, {
				allowedFieldIds,
				allowedVisibleLinkedFieldIds
			})
			addLinkedDatasetLabelData(optionData)
			return optionData
		}

		//the label could be html or a message key, so resolve into "sortLabel"
		//unless that was already defined
		options = _.map(options, opt => {
			return _.defaults({}, opt, {sortLabel: i18n.get(opt.label)})
		})
		//the DividedList uses the selects#siftFunction to perform search and sorting, so
		//return an object for arguments into that function
		return {
			options: options,
			sort: { field: 'sortLabel', direction: 'asc' },
			searchTerm: 'sortLabel'
		}
	},

	render() {
		this.$el.html(template(this.context()))

		//currently only dataset's support calculated columns
		//cannot repeat by calculated columns https://github.com/SpiderStrategies/Scoreboard/issues/20267#issuecomment-839914369
		this.$('.custom').toggleClass('is-not-displayed', this.dataset == null
			|| !!this.options.asRepeatBy)

		this.formView.start()

		this.$('.tooltip-header').after(this.dividedList.el)
		this.dividedList.render()
		this.dividedList.on('click', columnTypeOrFieldId => this.onFieldChosen(columnTypeOrFieldId))
		// This tooltip is as wide as it's widest node. The width shouldn't change
		// when the user is searching so apply a fixed width when the finder get's
		// focus because by that point, all of the nodes are in so the fixed width
		// will be accurate. This only needs done once.
		this.dividedList.finder.$el.find('input').one('focus', () => {
			// Set a fixed width based on its own width.
			this.$el.width(this.$el.width())
		})
		return this
	},

	context() {
		return {
			headerButtonLabel: this.options.headerButtonLabel || 'report.designer.newCalculation',
			header: this.isRepeatByForm ?
				i18n.get('report.designer.repeatColumnBy') :
				i18n.get('report.designer.addColumn')

		}
	},

	onFieldChosen(columnTypeOrFieldId) {
		this.selected = columnTypeOrFieldId
		if (this.options.onAdd) {
			// If an onAdd callback was passed in, call that before finishing
			let result = this.options.onAdd(columnTypeOrFieldId)

			if (_.isFunction(result.then)) {
				// If the onAdd callback returned a promise, give the tooltip
				// a busy/loading state and wait to finish things until the
				// promise resolves
				result.then(() => this.trigger('select', columnTypeOrFieldId))
			} else {
				this.trigger('select', columnTypeOrFieldId)
			}
		} else {
			this.trigger('select', columnTypeOrFieldId)
		}
	},

	onClickCustom() {
		this.trigger('details', this.selected)
	},

	change(value) {
		this.trigger('change', value)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.finder, this.formView], 'remove')
	}

})

function addLinkedDatasetLabelData(optionData) {
	_.each(optionData.options, opt => {
		if (opt.linkedDataset) {
			opt.secondaryLabel = `(${opt.linkedDataset})`
			opt.secondaryLabelClass = 'linked-dataset-label'
		}
	})
}

export default ReportColumnChooser
export { addLinkedDatasetLabelData }
ReportColumnChooser.__filename__ = 'reports/common/report-column-chooser.js'
